import { ApolloError } from '@apollo/client';
import { IonButton, IonIcon } from '@ionic/react';
import { documentAttachOutline } from 'ionicons/icons';
import { isNil } from 'lodash';
import { ReactElement, useCallback, useState } from 'react';
import {
  AttachmentSourceType,
  ChatMessage,
  ChatStatus,
  useCreateChatMessageMutation,
  useGetChatMessageDraftLazyQuery,
  useGetChatMessageLazyQuery,
  useGetChatMessageQuery,
} from '../../api/graphql/generated';
import { useUploadAttachmentLazy } from '../../api/hooks/attachment.hooks';
import { logger } from '../../util/logger';
import { AttachmentWrapper } from '../attachment/attachment-uploader';
import { SaveStatusToast } from '../toast/toast.gql';

interface ConversationMessageAttachmentUploaderProps {
  chatId: string;
  threadId: number | undefined;
  conversationId?: string; // the conversation is optional because a draft may not yet exist when using this component
  afterUpload?: () => void;
  beforeUpload?: () => void;
  onError?: () => void;
  onMessageCreated?: (message: ChatMessage) => void;
}

const ConversationMessageAttachmentUploader = (
  props: ConversationMessageAttachmentUploaderProps
): ReactElement => {
  const {
    afterUpload,
    beforeUpload,
    onMessageCreated,
    onError,
    chatId,
    threadId,
    conversationId,
  } = props;

  const uploadAttachment = useUploadAttachmentLazy();
  const [getChatMessage] = useGetChatMessageLazyQuery();
  const [getChatMessageDraft] = useGetChatMessageDraftLazyQuery();
  const { data: getChatMessageData } = useGetChatMessageQuery({
    variables: {
      input: {
        id: String(conversationId),
      },
    },
    skip: conversationId === undefined || conversationId === null,
  });
  const [createChatMessage] = useCreateChatMessageMutation();
  const [uploadError, setUploadError] = useState<
    string | boolean | ApolloError | undefined
  >(undefined);

  const createDraftMessage = useCallback(async () => {
    let attachmentSource = getChatMessageData?.getChatMessage;

    const result = await createChatMessage({
      variables: {
        input: {
          chatId,
          threadId: threadId === undefined ? undefined : String(threadId),
          message: '',
          status: ChatStatus.DRAFT,
        },
      },
    });

    attachmentSource = result.data?.createChatMessage;

    if (typeof onMessageCreated === 'function' && attachmentSource) {
      onMessageCreated(attachmentSource);
    }
    return attachmentSource;
  }, [
    chatId,
    createChatMessage,
    getChatMessageData?.getChatMessage,
    onMessageCreated,
    threadId,
  ]);

  const onAttachmentButtonClicked = useCallback(async () => {
    if (getChatMessageData?.getChatMessage === undefined) {
      await createDraftMessage();
    }
  }, [createDraftMessage, getChatMessageData?.getChatMessage]);

  const onFileChange = async (file: File) => {
    let attachmentSource = getChatMessageData?.getChatMessage;

    setUploadError(undefined);

    if (typeof beforeUpload === 'function') {
      beforeUpload();
    }

    if (attachmentSource === undefined) {
      attachmentSource = await createDraftMessage();
    }

    try {
      if (attachmentSource === undefined) {
        // if the code enters here, then there is a developer problem because we need to ensure that a conversation exists
        // before calling onFileChange. this can be done through the onClick method if needed.
        logger.error('could not upload attachment. conversation is undefined.');
        setUploadError(true);
        return;
      }

      await uploadAttachment(
        [
          {
            source: AttachmentSourceType.CHAT,
            sourceId: attachmentSource.chatId,
          },
          {
            source: AttachmentSourceType.CHAT_MESSAGE,
            sourceId: attachmentSource.id,
          },
        ],
        file,
        undefined,
        async () => {
          if (!attachmentSource) {
            // if this occurs, the cache will not be updated with the latest attachments
            logger.error(
              'could not refetch conversation. attachment source is not defined.',
              { chatId, threadId, conversationId }
            );
            return;
          }

          if (attachmentSource.status === ChatStatus.DRAFT) {
            // this is the start of a thread
            if (isNil(attachmentSource.threadId)) {
              // for new conversations we need to refetch the draft query with the chat id
              // because a thread draft is directly tied to the chat (no parent)
              await getChatMessageDraft({
                variables: {
                  input: {
                    chatId,
                  },
                },
                fetchPolicy: 'network-only',
              });
            } else {
              // reply drafts need to have the thread (parent) refetched since the thread message contains the draft data
              await getChatMessage({
                variables: {
                  input: {
                    id: attachmentSource.threadId,
                  },
                },
                fetchPolicy: 'network-only',
              });
            }
          } else {
            // if we're editing a thread message or editing replies
            // we just want to refetch the message since there's already an object reference in the cache
            await getChatMessage({
              variables: {
                input: {
                  id: attachmentSource.id,
                },
              },
              fetchPolicy: 'network-only',
            });
          }

          // we want the after upload to take place after we've done the FULL upload including updating the cache
          if (typeof afterUpload === 'function') {
            afterUpload();
          }
        },
        (error) => {
          if (error instanceof ApolloError) {
            logger.error('failed to upload attachment.', { error });
            setUploadError(error);
          } else {
            logger.error('failed to upload attachment.', { error });
            setUploadError(true);
          }

          if (typeof onError === 'function') {
            onError();
          }
        }
      );
    } catch (error) {
      logger.error('failed to upload attachment', { error });
      setUploadError(true);

      if (typeof onError === 'function') {
        onError();
      }
    }
  };

  return (
    <>
      <AttachmentWrapper
        uploadItem={
          <IonButton
            fill="clear"
            size="small"
            onClick={onAttachmentButtonClicked}
          >
            <IonIcon
              icon={documentAttachOutline}
              color="primary"
              className="chat-message-attachment-uploader-icon"
            />
          </IonButton>
        }
        uploaderProps={{
          onFileChange,
          inputProps: { accept: '.pdf,.png,.jpeg,.jpg' },
          clearFilesAfterChange: true,
        }}
      />
      <SaveStatusToast
        error={uploadError}
        defaultErrorMessageKey={'common:attachments_upload_failed_generic'}
      />
    </>
  );
};

export { ConversationMessageAttachmentUploader };
